import { useEffect, useState } from 'react';

/**
 * Custom hook to optimize page transitions and loading
 * This hook helps reduce the initial load time of pages by:
 * 1. Preloading critical assets
 * 2. Optimizing animations to run more efficiently
 * 3. Implementing progressive loading of content
 * 4. Ensuring pages always load properly
 */
const usePageTransition = () => {
  // State to track if the page is ready to show content
  const [isPageReady, setIsPageReady] = useState(false);
  
  useEffect(() => {
    // Set a flag in sessionStorage to indicate this page has been loaded before
    // This helps us know if we need to show full animations or simplified ones
    const hasVisitedBefore = sessionStorage.getItem('hasVisitedPage');
    
    if (!hasVisitedBefore) {
      // First visit - store that we've visited
      sessionStorage.setItem('hasVisitedPage', 'true');
    }
    
    // Force immediate rendering for return visits
    if (hasVisitedBefore === 'true') {
      setIsPageReady(true);
      document.body.classList.add('page-ready');
      return;
    }
    
    // For first visits, ensure the page loads with a guaranteed timeout
    const guaranteedTimeout = setTimeout(() => {
      setIsPageReady(true);
      document.body.classList.add('page-ready');
    }, 100); // Guaranteed to show content after 100ms
    
    // Try to optimize rendering when possible
    window.requestAnimationFrame(() => {
      setIsPageReady(true);
      document.body.classList.add('page-ready');
    });
    
    // Cleanup function
    return () => {
      clearTimeout(guaranteedTimeout);
      document.body.classList.remove('page-ready');
    };
  }, []);
  
  // Return whether this is a return visit and if the page is ready
  return {
    isReturnVisit: sessionStorage.getItem('hasVisitedPage') === 'true',
    isPageReady
  };
};

export default usePageTransition;
